import { useState, useRef, useEffect, useCallback } from "react";
import {
  X,
  Eraser,
  ArrowCounterClockwise,
  ArrowClockwise,
  Trash,
  FloppyDisk,
  Palette,
  PencilSimple,
  Minus,
  Plus,
} from "@phosphor-icons/react";
import "./HandwritingCanvas.css";

const COLORS = [
  { value: "#1a1a2e", label: "שחור" },
  { value: "#2563eb", label: "כחול" },
  { value: "#dc2626", label: "אדום" },
  { value: "#16a34a", label: "ירוק" },
  { value: "#9333ea", label: "סגול" },
  { value: "#ea580c", label: "כתום" },
];

const STROKE_WIDTHS = [2, 4, 6, 10];

function HandwritingCanvas({ strokes: initialStrokes = [], onSave, onClose }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [strokes, setStrokes] = useState(initialStrokes);
  const [currentStroke, setCurrentStroke] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [color, setColor] = useState(COLORS[0].value);
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [isEraser, setIsEraser] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  const resizeCanvas = useCallback(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    setCanvasSize({ width: rect.width, height: rect.height });
  }, []);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, [resizeCanvas]);

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

    // Draw ruled lines (subtle)
    ctx.strokeStyle = "#e5e7eb";
    ctx.lineWidth = 0.5;
    const lineSpacing = 32;
    for (let y = lineSpacing; y < canvas.height / dpr; y += lineSpacing) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width / dpr, y);
      ctx.stroke();
    }

    // Draw all saved strokes
    for (const stroke of strokes) {
      drawStroke(ctx, stroke);
    }

    // Draw current stroke being drawn
    if (currentStroke) {
      drawStroke(ctx, currentStroke);
    }
  }, [strokes, currentStroke]);

  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  const drawStroke = (ctx, stroke) => {
    if (!stroke.points || stroke.points.length < 2) return;

    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.width;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.globalCompositeOperation = stroke.eraser
      ? "destination-out"
      : "source-over";

    ctx.beginPath();
    ctx.moveTo(stroke.points[0].x, stroke.points[0].y);

    if (stroke.points.length === 2) {
      ctx.lineTo(stroke.points[1].x, stroke.points[1].y);
    } else {
      for (let i = 1; i < stroke.points.length - 1; i++) {
        const midX = (stroke.points[i].x + stroke.points[i + 1].x) / 2;
        const midY = (stroke.points[i].y + stroke.points[i + 1].y) / 2;
        ctx.quadraticCurveTo(stroke.points[i].x, stroke.points[i].y, midX, midY);
      }
      const last = stroke.points[stroke.points.length - 1];
      ctx.lineTo(last.x, last.y);
    }

    ctx.stroke();
    ctx.globalCompositeOperation = "source-over";
  };

  const getPointerPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0, pressure: 0.5 };
    const rect = canvas.getBoundingClientRect();

    let clientX, clientY, pressure;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
      pressure = e.touches[0].force || 0.5;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
      pressure = e.pressure || 0.5;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
      pressure,
    };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    const pos = getPointerPos(e);
    setIsDrawing(true);
    setCurrentStroke({
      points: [pos],
      color: isEraser ? "#ffffff" : color,
      width: isEraser ? strokeWidth * 3 : strokeWidth,
      eraser: isEraser,
    });
  };

  const handlePointerMove = (e) => {
    if (!isDrawing || !currentStroke) return;
    e.preventDefault();
    const pos = getPointerPos(e);
    setCurrentStroke((prev) => ({
      ...prev,
      points: [...prev.points, pos],
    }));
  };

  const handlePointerUp = (e) => {
    if (!isDrawing || !currentStroke) return;
    e.preventDefault();

    if (currentStroke.points.length >= 2) {
      setStrokes((prev) => [...prev, currentStroke]);
      setUndoStack([]);
    }

    setCurrentStroke(null);
    setIsDrawing(false);
  };

  const handleUndo = () => {
    if (strokes.length === 0) return;
    const lastStroke = strokes[strokes.length - 1];
    setStrokes((prev) => prev.slice(0, -1));
    setUndoStack((prev) => [...prev, lastStroke]);
  };

  const handleRedo = () => {
    if (undoStack.length === 0) return;
    const lastUndo = undoStack[undoStack.length - 1];
    setUndoStack((prev) => prev.slice(0, -1));
    setStrokes((prev) => [...prev, lastUndo]);
  };

  const handleClear = () => {
    if (strokes.length === 0) return;
    setUndoStack([...strokes]);
    setStrokes([]);
  };

  const handleSave = () => {
    onSave(strokes, canvasSize);
  };

  const toggleEraser = () => {
    setIsEraser((prev) => !prev);
    setShowColorPicker(false);
  };

  const adjustWidth = (delta) => {
    const currentIndex = STROKE_WIDTHS.indexOf(strokeWidth);
    const newIndex = Math.max(0, Math.min(STROKE_WIDTHS.length - 1, currentIndex + delta));
    setStrokeWidth(STROKE_WIDTHS[newIndex]);
  };

  return (
    <div className="handwriting-modal-overlay">
      <div className="handwriting-modal">
        {/* Header */}
        <div className="handwriting-header">
          <div className="handwriting-header-right">
            <PencilSimple size={20} weight="duotone" />
            <span>כתיבה חופשית</span>
          </div>
          <div className="handwriting-header-left">
            <button
              className="handwriting-save-btn"
              onClick={handleSave}
              title="שמור וסגור"
            >
              <FloppyDisk size={18} weight="bold" />
              <span>שמור</span>
            </button>
            <button
              className="handwriting-close-btn"
              onClick={onClose}
              title="סגור ללא שמירה"
            >
              <X size={20} weight="bold" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="handwriting-toolbar">
          <div className="toolbar-group">
            <button
              className={`toolbar-btn ${!isEraser ? "active" : ""}`}
              onClick={() => { setIsEraser(false); setShowColorPicker(false); }}
              title="עט"
            >
              <PencilSimple size={18} weight={!isEraser ? "fill" : "regular"} />
            </button>
            <button
              className={`toolbar-btn ${isEraser ? "active eraser" : ""}`}
              onClick={toggleEraser}
              title="מחק"
            >
              <Eraser size={18} weight={isEraser ? "fill" : "regular"} />
            </button>
          </div>

          <div className="toolbar-divider" />

          <div className="toolbar-group">
            <button
              className="toolbar-btn"
              onClick={() => adjustWidth(-1)}
              disabled={strokeWidth === STROKE_WIDTHS[0]}
              title="דק יותר"
            >
              <Minus size={16} />
            </button>
            <div className="stroke-width-preview">
              <div
                className="stroke-width-dot"
                style={{
                  width: strokeWidth * 2,
                  height: strokeWidth * 2,
                  backgroundColor: isEraser ? "#9ca3af" : color,
                }}
              />
            </div>
            <button
              className="toolbar-btn"
              onClick={() => adjustWidth(1)}
              disabled={strokeWidth === STROKE_WIDTHS[STROKE_WIDTHS.length - 1]}
              title="עבה יותר"
            >
              <Plus size={16} />
            </button>
          </div>

          <div className="toolbar-divider" />

          <div className="toolbar-group color-group">
            <button
              className={`toolbar-btn color-toggle ${showColorPicker ? "active" : ""}`}
              onClick={() => setShowColorPicker((prev) => !prev)}
              title="צבע"
            >
              <Palette size={18} weight="duotone" />
              <div
                className="current-color-dot"
                style={{ backgroundColor: color }}
              />
            </button>
            {showColorPicker && (
              <div className="color-picker-popup">
                {COLORS.map((c) => (
                  <button
                    key={c.value}
                    className={`color-option ${color === c.value ? "selected" : ""}`}
                    style={{ backgroundColor: c.value }}
                    onClick={() => {
                      setColor(c.value);
                      setIsEraser(false);
                      setShowColorPicker(false);
                    }}
                    title={c.label}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="toolbar-divider" />

          <div className="toolbar-group">
            <button
              className="toolbar-btn"
              onClick={handleUndo}
              disabled={strokes.length === 0}
              title="בטל"
            >
              <ArrowCounterClockwise size={18} />
            </button>
            <button
              className="toolbar-btn"
              onClick={handleRedo}
              disabled={undoStack.length === 0}
              title="בצע שוב"
            >
              <ArrowClockwise size={18} />
            </button>
          </div>

          <div className="toolbar-divider" />

          <button
            className="toolbar-btn danger"
            onClick={handleClear}
            disabled={strokes.length === 0}
            title="נקה הכל"
          >
            <Trash size={18} />
          </button>
        </div>

        {/* Canvas */}
        <div className="handwriting-canvas-container" ref={containerRef}>
          <canvas
            ref={canvasRef}
            className="handwriting-canvas"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{ touchAction: "none" }}
          />
        </div>
      </div>
    </div>
  );
}

export function HandwritingPreview({ strokes, canvasSize, onClick }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!strokes || strokes.length === 0 || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    const previewWidth = canvas.parentElement.clientWidth;
    const previewHeight = 120;

    canvas.width = previewWidth * dpr;
    canvas.height = previewHeight * dpr;
    canvas.style.width = `${previewWidth}px`;
    canvas.style.height = `${previewHeight}px`;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, previewWidth, previewHeight);

    const scaleX = canvasSize?.width ? previewWidth / canvasSize.width : 0.3;
    const scaleY = canvasSize?.height ? previewHeight / canvasSize.height : 0.3;
    const scale = Math.min(scaleX, scaleY);

    ctx.save();
    ctx.scale(scale, scale);

    for (const stroke of strokes) {
      if (!stroke.points || stroke.points.length < 2) continue;

      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.width;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.globalCompositeOperation = stroke.eraser
        ? "destination-out"
        : "source-over";

      ctx.beginPath();
      ctx.moveTo(stroke.points[0].x, stroke.points[0].y);

      for (let i = 1; i < stroke.points.length - 1; i++) {
        const midX = (stroke.points[i].x + stroke.points[i + 1].x) / 2;
        const midY = (stroke.points[i].y + stroke.points[i + 1].y) / 2;
        ctx.quadraticCurveTo(stroke.points[i].x, stroke.points[i].y, midX, midY);
      }
      const last = stroke.points[stroke.points.length - 1];
      ctx.lineTo(last.x, last.y);
      ctx.stroke();
      ctx.globalCompositeOperation = "source-over";
    }

    ctx.restore();
  }, [strokes, canvasSize]);

  if (!strokes || strokes.length === 0) return null;

  return (
    <div className="handwriting-preview" onClick={onClick}>
      <canvas ref={canvasRef} className="handwriting-preview-canvas" />
      <span className="handwriting-preview-label">לחץ לעריכה</span>
    </div>
  );
}

export default HandwritingCanvas;
